import { useState, useEffect, useCallback } from 'react';
import { useCookies } from 'react-cookie';

function getRootDomain() {
  const parts = window.location.hostname.split('.');
  if (parts.length > 2) {
    return parts.slice(-2).join('.');
  }
  return window.location.hostname;
}

/**
 * Hook to check across main- and sub-domain based on cookie if a certain cookie was set
 * and delay when it gets set.
 * @param cookieName name of the cookie to check if set
 * @param delayInMs the time until the shouldShow will be set to false once setDelay is called
 * @returns {UseDelayedEventCheckValues}
 */
export default function useDelayedEventCheck(cookieName: string, delayInMs: number) {
  const [shouldShow, setShouldShow] = useState<boolean>(true);

  const [cookies, setCookie] = useCookies();

  useEffect(() => {
    const delayUntil = cookies[cookieName];
    if (delayUntil) {
      const timestamp = parseInt(delayUntil, 10);
      if (!Number.isNaN(timestamp) && Date.now() < timestamp) setShouldShow(false);
    }
  }, [cookieName, cookies]);

  const setDelay = useCallback(
    (delayOverrideInMs?: number) => {
      const expTimestamp = Date.now() + (delayOverrideInMs || delayInMs);
      setCookie(cookieName, String(expTimestamp), {
        domain: getRootDomain(),
        sameSite: 'lax',
        path: '/',
        expires: new Date(expTimestamp),
      });
    },
    [cookieName, delayInMs, setCookie],
  );

  return { shouldShow, setDelay };
}

export type UseDelayedEventCheckValues = ReturnType<typeof useDelayedEventCheck>;
